import classNames from 'classnames'
import i18next from 'i18next'
import { HTMLAttributes, useMemo } from 'react'
import { FormSelectA11y } from '../form-select/FormSelectA11y'
import { IconButton } from '../icon-button'
import styles from './index.module.css'

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  page: number
  count: number
  rowsPerPage: number
  rowsPerPageOptions?: number[]
  onRowsPerPageChange?: (page: number) => void
  onPageChange?: (page: number) => void
  onChange?: (page: number, rowsPerPage: number) => void
}

export const TablePagination = ({
  page,
  count,
  rowsPerPage,
  rowsPerPageOptions,
  className,
  onChange,
  onPageChange,
  onRowsPerPageChange,
  ...props
}: Props) => {
  const numberOfPages = useMemo(() => {
    if (count < rowsPerPage) {
      return [1]
    }
    const pages = Array.from(Array(Math.ceil(count / rowsPerPage)).keys())
    pages[pages.length] = pages.length
    pages.shift()
    return pages
  }, [count, rowsPerPage])

  const onSelect = (value?: string | number) => {
    if (value) {
      onRowsPerPageChange?.(Number(value))
      onPageChange?.(1)
      onChange?.(1, Number(value))
    }
  }

  const handlePageChange = (newPage: number) => {
    onPageChange?.(newPage)
    onChange?.(newPage, rowsPerPage)
  }
  const isLastPage = page * rowsPerPage >= count
  return (
    <div className={classNames(className, styles.container)} role="navigation" {...props}>
      {rowsPerPageOptions && (
        <FormSelectA11y
          className="min-w-16"
          openAbove={true}
          selected={rowsPerPage}
          aria-label="Select items per page"
          showNoneOption={false}
          onSelect={onSelect}
          options={rowsPerPageOptions.map((rowsPerPageOption) => ({
            label: rowsPerPageOption,
            code: rowsPerPageOption
          }))}
        />
      )}
      <span>{i18next.t('glow.goToPage')}</span>
      {numberOfPages.length > 1 && (
        <FormSelectA11y
          className="min-w-16"
          showNoneOption={false}
          onSelect={(code) => handlePageChange(code as number)}
          options={numberOfPages.map((numberOfPage) => ({ code: numberOfPage, label: numberOfPage }))}
          openAbove={true}
          selected={page}
          aria-label="Select page"
        />
      )}
      <span>{`${i18next.t('glow.page')} ${page} ${i18next.t('glow.of')} ${numberOfPages.length}`}</span>
      <IconButton
        disabled={page === 1}
        aria-label="Previous page"
        onClick={() => handlePageChange(page - 1)}
        className="ml-4 mr-2"
        icon="chevron-left"
        size="sm"
      />
      <IconButton
        disabled={isLastPage}
        className="ml-2"
        aria-label="Next page"
        onClick={() => handlePageChange(page + 1)}
        icon="chevron-right"
        size="sm"
      />
    </div>
  )
}
